import { SourceDocumentUIPart, SourceUrlUIPart } from "ai";

import type {
  NoxusUIMessage,
  NoxusMessagePart,
  NoxusDataTypes,
} from "./noxus-message-types";

export type ToolDisplayPreset = "minimal" | "execution" | "tracing";

export type MessagePart = NoxusMessagePart;
type FilePart = Extract<MessagePart, { type: "file" }>;

type SourceGroup = {
  type: "source-group";
  parts: (SourceUrlUIPart | SourceDocumentUIPart)[];
};
type FileGroup = { type: "file-group"; parts: FilePart[] };
export type GroupedPart =
  | SourceGroup
  | FileGroup
  | Exclude<MessagePart, SourceUrlUIPart | SourceDocumentUIPart | FilePart>;

/**
 * Group consecutive source and file parts for display.
 * All other parts (including data-* and tool-*) pass through as-is.
 */
export function groupConsecutiveParts(parts: MessagePart[]): GroupedPart[] {
  const result: GroupedPart[] = [];
  let currentSourceGroup: (SourceUrlUIPart | SourceDocumentUIPart)[] = [];
  let currentFileGroup: FilePart[] = [];

  const flushGroups = () => {
    if (currentSourceGroup.length > 0) {
      result.push({ type: "source-group", parts: currentSourceGroup });
      currentSourceGroup = [];
    }
    if (currentFileGroup.length > 0) {
      result.push({ type: "file-group", parts: currentFileGroup });
      currentFileGroup = [];
    }
  };

  for (const part of parts) {
    if (part.type === "source-url" || part.type === "source-document") {
      if (currentFileGroup.length > 0) {
        result.push({ type: "file-group", parts: currentFileGroup });
        currentFileGroup = [];
      }
      currentSourceGroup.push(part as SourceUrlUIPart | SourceDocumentUIPart);
    } else if (part.type === "file") {
      if (currentSourceGroup.length > 0) {
        result.push({ type: "source-group", parts: currentSourceGroup });
        currentSourceGroup = [];
      }
      currentFileGroup.push(part as FilePart);
    } else {
      flushGroups();
      result.push(
        part as Exclude<
          MessagePart,
          SourceUrlUIPart | SourceDocumentUIPart | FilePart
        >
      );
    }
  }

  flushGroups();

  return result;
}

/**
 * Whether a reasoning part is the agent's *narration* — status text it wrote
 * for the user, which "only show the final answer" mode folds into the chain
 * instead of letting it read as the answer. Tagged by the backend
 * (`NARRATION_METADATA`) on both the live stream and the reload path. Narration
 * is not model thinking, so "hide reasoning" leaves it alone.
 */
export function isNarrationPart(part: GroupedPart | MessagePart): boolean {
  if (part.type !== "reasoning") return false;
  return part.providerMetadata?.noxus?.narration === true;
}

// OpenAI sends some reasoning with no summary, only its encrypted signature;
// the part stays in the history for replay but has nothing to show.
export function isEmptyThought(part: GroupedPart | MessagePart): boolean {
  return (
    part.type === "reasoning" &&
    !isNarrationPart(part) &&
    part.state !== "streaming" &&
    !part.text.trim()
  );
}

/** Look up a typed data part by type and id (for tool-associated metadata). */
export function findDataPart<K extends keyof NoxusDataTypes>(
  parts: NoxusMessagePart[],
  type: `data-${K}`,
  id: string
): Extract<NoxusMessagePart, { type: `data-${K}` }> | undefined {
  return parts.find(
    (p): p is Extract<NoxusMessagePart, { type: `data-${K}` }> =>
      p.type === type && "id" in p && p.id === id
  );
}

/** Concatenate the text of all text parts of a message. */
export function getTextFromParts(parts: MessagePart[], separator = ""): string {
  return parts
    .filter(
      (p): p is Extract<MessagePart, { type: "text" }> => p.type === "text"
    )
    .map((p) => p.text)
    .join(separator);
}

/**
 * Carry the rating stored on each DB message onto the UI messages the widget
 * renders, matched by the row id the backend stamps on every UI message.
 *
 * Only the polarity: the comment stays server-side, since a rating carries no
 * author and may have been written by an internal reviewer of the conversation.
 */
export function withStoredFeedback(
  messages: NoxusUIMessage[],
  dbMessages: { id: string; is_positive?: boolean | null }[] | undefined
): NoxusUIMessage[] {
  if (!dbMessages?.length) {
    return messages;
  }
  const ratingByRow = new Map(
    dbMessages.map((row) => [row.id, row.is_positive] as const)
  );
  return messages.map((message) => {
    if (message.role !== "assistant") {
      return message;
    }
    const rowId = message.metadata?.message_id;
    const isPositive = rowId ? ratingByRow.get(rowId) : undefined;
    if (isPositive === null || isPositive === undefined) {
      return message;
    }
    return {
      ...message,
      metadata: { ...message.metadata, feedback: { is_positive: isPositive } },
    };
  });
}

function sumWorkedMs(run: NoxusUIMessage[]): number | undefined {
  const known = run
    .map((message) => message.metadata?.worked_ms)
    .filter((value) => value !== undefined);
  return known.length
    ? known.reduce((total, value) => total + value, 0)
    : undefined;
}

// A fold is a new message, so a finished turn would re-render on every streamed
// token. Reuse it while its run is the same messages.
const foldCache = new WeakMap<
  NoxusUIMessage,
  { run: NoxusUIMessage[]; folded: NoxusUIMessage }
>();

function foldRun(run: NoxusUIMessage[]): NoxusUIMessage {
  if (run.length === 1) {
    return run[0];
  }
  const cached = foldCache.get(run[0]);
  if (
    cached?.run.length === run.length &&
    cached.run.every((message, i) => message === run[i])
  ) {
    return cached.folded;
  }
  const folded = buildFold(run);
  foldCache.set(run[0], { run, folded });
  return folded;
}

function buildFold(run: NoxusUIMessage[]): NoxusUIMessage {
  const latest = run[run.length - 1];
  return {
    ...run[0],
    parts: run.flatMap((message) => message.parts),
    metadata: {
      ...Object.assign({}, ...run.map((message) => message.metadata)),
      message_id: latest.metadata?.message_id,
      feedback: latest.metadata?.feedback,
      worked_ms: sumWorkedMs(run),
    },
  };
}

/**
 * Consecutive assistant messages folded into one, so a chain of thought can
 * span message boundaries, each kept with the index of the first it folds.
 * The folded reply acts on the latest message's row and rating, not the row
 * the fold began with; the time worked is summed.
 */
export function foldAssistantMessages(
  messages: NoxusUIMessage[]
): { msg: NoxusUIMessage; originalIndex: number }[] {
  const result: { msg: NoxusUIMessage; originalIndex: number }[] = [];
  let index = 0;
  while (index < messages.length) {
    const start = index;
    index += 1;
    if (messages[start].role === "assistant") {
      while (index < messages.length && messages[index].role === "assistant") {
        index += 1;
      }
    }
    result.push({
      msg: foldRun(messages.slice(start, index)),
      originalIndex: start,
    });
  }
  return result;
}

export const getAllSources = (parts: MessagePart[]) => {
  return groupConsecutiveParts(parts)
    .filter((part) => part.type === "source-group")
    .flatMap((part) => part.parts) as (
    | SourceUrlUIPart
    | SourceDocumentUIPart
  )[];
};

/**
 * Get the canonical URL for a source part.
 * Web sources use the url field directly; document sources use the metadata url
 * or fall back to a file preview URL.
 */
export function getSourceUrl(
  source: SourceUrlUIPart | SourceDocumentUIPart
): string {
  if (source.type === "source-url") return source.url;
  const meta = source.providerMetadata?.noxus as
    | { url?: string; file_id?: string }
    | undefined;
  return meta?.url || `/api/backend/file/${meta?.file_id}?preview=true`;
}

/**
 * Deduplicate sources by URL, keeping the first occurrence.
 */
export function deduplicateSourcesByUrl(
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[]
): (SourceUrlUIPart | SourceDocumentUIPart)[] {
  const seen = new Set<string>();
  return sources.filter((s) => {
    const url = getSourceUrl(s);
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

/**
 * Merge contiguous sources that share the same URL into a single source group.
 * Returns a new array where each run of same-URL sources is collapsed to its
 * first entry (preserving order).
 */
export function mergeContiguousSameUrlSources(
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[]
): (SourceUrlUIPart | SourceDocumentUIPart)[] {
  if (sources.length <= 1) return sources;
  const result: (SourceUrlUIPart | SourceDocumentUIPart)[] = [sources[0]];
  let prevUrl = getSourceUrl(sources[0]);
  for (let i = 1; i < sources.length; i++) {
    const url = getSourceUrl(sources[i]);
    if (url !== prevUrl) {
      result.push(sources[i]);
      prevUrl = url;
    }
  }
  return result;
}
